import orderBy from 'lodash.orderby'
import dayjs from 'dayjs'
import { RegistrationStatus } from '~/enums/registrationStatus'
import { MAX_TH_LEVEL, HIDDEN_EVENT_IDS } from '~/utils/constants'
import { getRosterMinSizeFromSegment, getRosterMaxSizeFromSegment } from '~/utils/segment'

// Picks the registration phase + derived status to surface for a thSegment's
// children: the first child whose registration is currently open, otherwise the
// first child that has any registration phase. Shared by the clan-based active
// segment and the tab-specific registration countdown.
function resolveRegistration(thSegment, now) {
	const withPhases = thSegment.children.filter(
		(s) => s.registrationPhases?.length > 0 && s.registrationType === 'allowed'
	)
	const open = withPhases.filter((s) => s.registrationStatus === 'open')
	const segmentWithReg = open.length > 0 ? open[0] : withPhases[0]
	const phase = segmentWithReg?.registrationPhases?.[0]

	let status
	if (phase) {
		const from = dayjs(phase.date?.from)
		const to = dayjs(phase.date?.to)
		if (now.isBefore(from)) {
			status = RegistrationStatus.BEFORE
		} else if (now.isAfter(to)) {
			status = RegistrationStatus.AFTER
		} else {
			status = RegistrationStatus.OPEN
		}
	}
	return { phase, status }
}

export const state = () => ({
	event: null,
	events: [],
	activeEvent: null,
	segments: [],
	thSegments: [],
	activeSegment: null,
	eventMonthString: null,
	registrationSegment: null,
	registrationPhase: null,
	registrationStatus: null,
	// TH level backing the clan-based (default) registration countdown.
	activeThLevel: null,
	// Registration countdown for the home tab the user is currently viewing,
	// independent of activeSegment / action buttons. `null` => use the default.
	// Shape: { thLevel, phase, status }.
	tabRegistration: null,
})

export const mutations = {
	setEvent(state, event) {
		state.event = event
	},
	setEvents(state, events) {
		state.events = events
	},
	setActiveEvent(state, event) {
		state.activeEvent = event
	},
	setSegments(state, segments) {
		state.segments = segments
	},
	setThSegments(state, thSegments) {
		state.thSegments = thSegments
	},
	setActiveSegment(state, segment) {
		state.activeSegment = segment
	},
	setRegistrationSegment(state, registrationSegment) {
		state.registrationSegment = registrationSegment
	},
	setRegistrationPhase(state, registrationPhase) {
		state.registrationPhase = registrationPhase
	},
	setRegistrationStatus(state, registrationStatus) {
		state.registrationStatus = registrationStatus
	},
	setActiveThLevel(state, activeThLevel) {
		state.activeThLevel = activeThLevel
	},
	setTabRegistration(state, tabRegistration) {
		state.tabRegistration = tabRegistration
	},
}

export const actions = {
	async getSegmentsForEvent({ commit }, eventId) {
		try {
			const { data: segments } = await this.$api.events.segment.getSegmentsByLevel(eventId, 0)

			const sortedSegments = orderBy(segments, 'date.from')
			commit('setSegments', sortedSegments)
		} catch (err) {
			await this.$logApi.createLogMessage(`getSegmentsForEvent failed`, {
				error: JSON.stringify(err),
			})
		}
	},

	async setActiveSegmentForEvent({ commit, state, rootState }, thLevel = undefined) {
		try {
			if (!state.segments.length) {
				return
			}

			if (thLevel === undefined) {
				thLevel = MAX_TH_LEVEL
			}

			let thSegment

			// Check if the latestRoster belong to a TEST segment
			// If so, we need to use the TEST segment
			const latestRoster = rootState?.user?.latestRoster
			if (latestRoster?.segment?.parent?.name?.includes('[TEST]')) {
				thSegment = state.segments.find((s) => s.id === latestRoster.segment?.parent?.id)
			}

			// If the latestRoster belong to the activeEvent, we need to use the segment of the latestRoster
			if (latestRoster?.event?.id === state.activeEvent?.id) {
				thSegment = state.segments.find((s) => s.id === latestRoster.segment?.parent?.id)
			}

			// If the latestRoster does not belong to a TEST segment
			// we need to find the segment with the same thLevel
			if (!thSegment) {
				thSegment = state.segments
					.filter((s) => !s.name.includes('[TEST]'))
					.find((s) => s.configuration?.thLevel === thLevel)
			}

			// if wasn't possible to find the thSegment from players thLevel and the latestRoster has a segment,
			// we need to use the parent segment of the latestRoster
			if (!thSegment && latestRoster?.segment?.id) {
				thSegment = state.segments.find((s) => s.id === latestRoster.segment?.parent?.id)
			}

			// is wasn't possible to find te thSegment
			// will be used the first segment with children
			if (!thSegment) {
				thSegment = state.segments
					.filter((s) => !s.name.includes('[TEST]'))
					.find((s) => s.children.length)
			}

			if (!thSegment) {
				return
			}

			const now = dayjs()
			let activeSegment = thSegment.children.find((s) => {
				const from = dayjs(s.date?.from)
				const to = dayjs(s.date?.to)
				return now.isAfter(from) && now.isBefore(to)
			})

			if (!activeSegment) {
				// Find the first upcoming segment
				const upcomingSegment = thSegment.children.find((s) =>
					now.isBefore(dayjs(s.date.from))
				)

				// If no upcoming segment, take the last one
				if (upcomingSegment) {
					activeSegment = upcomingSegment
				} else {
					activeSegment = thSegment.children[thSegment.children.length - 1]
				}
			}

			const { phase: registrationPhase, status: regStatus } = resolveRegistration(
				thSegment,
				now
			)

			commit('setActiveSegment', activeSegment)
			commit('setActiveThLevel', thSegment.configuration?.thLevel ?? thLevel)
			commit('setRegistrationPhase', registrationPhase)
			commit('setRegistrationStatus', regStatus)
			commit('setThSegments', thSegment.children)
		} catch (err) {
			await this.$logApi.createLogMessage(`setActiveSegmentForEvent failed`, {
				error: JSON.stringify(err),
			})
		}
	},

	// Resolves ONLY the registration countdown for a given TH level (the home tab
	// being viewed), without touching activeSegment / registration buttons. Pass
	// a nullish thLevel to clear it and fall back to the clan-based countdown.
	setTabRegistrationForLevel({ commit, state }, thLevel) {
		if (thLevel === undefined || thLevel === null || !state.segments.length) {
			commit('setTabRegistration', null)
			return
		}

		const thSegment = state.segments
			.filter((s) => !s.name.includes('[TEST]'))
			.find((s) => s.configuration?.thLevel === thLevel)

		if (!thSegment?.children?.length) {
			commit('setTabRegistration', null)
			return
		}

		const { phase, status } = resolveRegistration(thSegment, dayjs())
		commit('setTabRegistration', {
			thLevel: thSegment.configuration?.thLevel ?? thLevel,
			phase,
			status,
		})
	},

	async getEvents({ dispatch, commit }, eventId) {
		try {
			const now = dayjs()
			const { data: events } = await this.$api.events.event.getAll()
			let activeEvent

			// If eventId is provided, we need to check if the event is already in the state
			// If not, we need to fetch it from the API and add it to the state.
			// This is the only path that may resolve a HIDDEN_EVENT_IDS event, so
			// hard-coded (targeted) players still load their event by explicit id.
			if (eventId) {
				activeEvent = events.find((e) => e.id === eventId)
				if (!activeEvent) {
					const { data: _event } = await this.$api.events.event.getById(eventId)
					events.push(_event)
					activeEvent = _event
				}
			}

			// Hidden events are kept `active` so they're loadable by id above, but
			// must never be picked up by the general, time-based selectors below.
			const selectableEvents = events.filter((e) => !HIDDEN_EVENT_IDS.includes(e.id))

			if (!activeEvent) {
				activeEvent = selectableEvents.find((s) => {
					const from = dayjs(s.date?.from)
					const to = dayjs(s.date?.to)
					return now.isAfter(from) && now.isBefore(to)
				})
			}

			if (!activeEvent) {
				// Find the first upcoming segment
				const upcomingEvent = selectableEvents.find((s) => now.isBefore(dayjs(s.date.from)))

				// If no upcoming segment, take the last one
				if (upcomingEvent) {
					activeEvent = upcomingEvent
				} else {
					activeEvent = selectableEvents[selectableEvents.length - 1]
				}
			}

			// Keep a hidden event in the list only when it is the active (targeted)
			// event, so it stays out of general event pickers (e.g. competition page)
			// for everyone else while remaining visible to the targeted player.
			const visibleEvents = events.filter(
				(e) => !HIDDEN_EVENT_IDS.includes(e.id) || e.id === activeEvent?.id
			)

			commit('setEvents', visibleEvents)
			commit('setActiveEvent', activeEvent)

			await dispatch('getSegmentsForEvent', activeEvent?.id)
		} catch (err) {
			// eslint-disable-next-line no-console
			console.error('getEvents failed', err)
			await this.$logApi.createLogMessage(`getEvents failed`, {
				error: JSON.stringify(err),
			})
		}
	},
}

export const getters = {
	rosterMinSize(state) {
		if (!state.activeSegment) return null
		return getRosterMinSizeFromSegment(state.activeSegment)
	},
	rosterMaxSize(state) {
		if (!state.activeSegment) return null
		return getRosterMaxSizeFromSegment(state.activeSegment)
	},
}
